/**
 * Equipment Card
 * Displays equipment item with purchase/equip options
 */

import { EquipmentDefinition, EquipmentStats } from '@/types';
import { formatCurrency, EQUIPMENT_DATA } from '@/data';
import { SLOT_CONFIG } from '@/data/equipment/slotConfig';
import { equipmentManager } from '@/systems';
import { useOwnedEquipment, useEquippedGear, useCurrency } from '@/state';

interface EquipmentCardProps {
  equipment: EquipmentDefinition;
}

export function EquipmentCard({ equipment }: EquipmentCardProps) {
  const ownedEquipment = useOwnedEquipment();
  const equippedGear = useEquippedGear();
  const currency = useCurrency();

  const isOwned = !!ownedEquipment[equipment.id];
  const isEquipped = equippedGear[equipment.slot] === equipment.id;
  const canAfford = currency >= equipment.cost;
  const canPurchase = equipmentManager.canPurchase(equipment.id);
  const ownedQty = ownedEquipment[equipment.id]?.quantity ?? 0;

  // Available quantity (owned minus equipped on player + buddies)
  const availableQty = isOwned ? (equipmentManager.getAvailableQuantities()[equipment.id] ?? 0) : 0;

  // Get currently equipped item in same slot for comparison
  const equippedId = equippedGear[equipment.slot];
  const equippedItem = equippedId && equippedId !== equipment.id ? EQUIPMENT_DATA[equippedId] : null;

  const handlePurchase = () => {
    equipmentManager.purchaseEquipment(equipment.id);
  };

  const handleEquip = () => {
    equipmentManager.equipItem(equipment.id);
  };

  const handleUnequip = () => {
    equipmentManager.unequipSlot(equipment.slot);
  };

  return (
    <div className={`bg-gray-800 rounded-lg p-4 border-2 ${isEquipped ? 'border-blue-500' : 'border-transparent'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-sm text-gray-400">{SLOT_CONFIG[equipment.slot].icon} {SLOT_CONFIG[equipment.slot].label}</div>
          <div className="font-bold text-white text-lg">{equipment.name}</div>
        </div>
        {isEquipped && (
          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
            Equipped
          </span>
        )}
      </div>

      <p className="text-gray-400 text-sm mb-3">{equipment.description}</p>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        {(equipment.stats.fishingSpeed || 0) > 0 && (
          <div className="text-green-400">
            ⚡ Speed: +{Math.round((equipment.stats.fishingSpeed || 0) * 100)}%
          </div>
        )}
        {(equipment.stats.catchChance || 0) > 0 && (
          <div className="text-blue-400">
            🎯 Catch: +{Math.round((equipment.stats.catchChance || 0) * 100)}%
          </div>
        )}
        {(equipment.stats.qualityBonus || 0) > 0 && (
          <div className="text-purple-400">
            ⭐ Quality: +{Math.round((equipment.stats.qualityBonus || 0) * 100)}%
          </div>
        )}
        {(equipment.stats.valueBonus || 0) > 0 && (
          <div className="text-yellow-400">
            💰 Value: +{Math.round((equipment.stats.valueBonus || 0) * 100)}%
          </div>
        )}
      </div>

      {/* Comparison vs equipped */}
      {!isEquipped && (
        <ComparisonPanel newStats={equipment.stats} equippedItem={equippedItem} />
      )}

      {/* Requirements */}
      {equipment.requires && (
        <div className="text-xs text-gray-500 mb-3">
          {equipment.requires.fishCaught && (
            <div>Requires: {equipment.requires.fishCaught} fish caught</div>
          )}
          {equipment.requires.previousTier && (
            <div>Requires previous tier</div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-yellow-400 font-bold">{formatCurrency(equipment.cost)}</div>
          {isOwned && (
            <div className="text-xs text-gray-400">
              Owned: {ownedQty}
              {!equipment.isConsumable && ownedQty > 1 && (
                <span className="text-green-400 ml-1">({availableQty} avail)</span>
              )}
            </div>
          )}
        </div>
        {equipment.isConsumable ? (
          <button
            onClick={handlePurchase}
            disabled={!canPurchase}
            className={`px-4 py-2 rounded font-bold transition-colors ${
              canPurchase
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {canAfford ? `Buy ${equipment.purchaseQuantity || 10}` : 'Too Expensive'}
          </button>
        ) : !isOwned ? (
          <button
            onClick={handlePurchase}
            disabled={!canPurchase}
            className={`px-4 py-2 rounded font-bold transition-colors ${
              canPurchase
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {canAfford ? 'Purchase' : 'Too Expensive'}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {isEquipped ? (
              <button
                onClick={handleUnequip}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded font-bold transition-colors text-sm"
              >
                Unequip
              </button>
            ) : (
              <button
                onClick={handleEquip}
                disabled={availableQty === 0}
                className={`px-3 py-2 rounded font-bold transition-colors text-sm ${
                  availableQty > 0
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                Equip
              </button>
            )}
            <button
              onClick={handlePurchase}
              disabled={!canPurchase}
              className={`px-3 py-2 rounded font-bold transition-colors text-sm ${
                canPurchase
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {canAfford ? 'Buy Another' : 'Too Expensive'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const STAT_LABELS: { key: keyof EquipmentStats; label: string; icon: string }[] = [
  { key: 'fishingSpeed', label: 'Speed', icon: '⚡' },
  { key: 'catchChance', label: 'Catch', icon: '🎯' },
  { key: 'qualityBonus', label: 'Quality', icon: '⭐' },
  { key: 'valueBonus', label: 'Value', icon: '💰' },
];

function ComparisonPanel({
  newStats,
  equippedItem,
}: {
  newStats: EquipmentStats;
  equippedItem: EquipmentDefinition | null;
}) {
  const oldStats: EquipmentStats = equippedItem?.stats ?? {};
  const comparedTo = equippedItem ? equippedItem.name : 'Empty Slot';

  // Collect diffs for summary
  const diffs: { label: string; diff: number }[] = [];
  for (const { key, label } of STAT_LABELS) {
    const diff = (newStats[key] || 0) - (oldStats[key] || 0);
    if (diff !== 0) {
      diffs.push({ label, diff });
    }
  }

  // No differences and comparing against an empty slot with no stats - skip panel
  if (diffs.length === 0 && !equippedItem) return null;

  return (
    <div className="text-xs mb-3 bg-gray-700/50 rounded p-2">
      <div className="text-gray-500 mb-1">vs. {comparedTo}:</div>
      {STAT_LABELS.map(({ key, label, icon }) => {
        const newVal = newStats[key] || 0;
        const oldVal = oldStats[key] || 0;
        const diff = newVal - oldVal;
        if (newVal === 0 && oldVal === 0) return null;
        const pct = Math.round(diff * 100);
        const color =
          diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-500';
        return (
          <div key={key} className={color}>
            {icon} {label}: {diff > 0 ? '+' : ''}{pct}%{diff === 0 ? ' (same)' : ''}
          </div>
        );
      })}
      {diffs.length > 0 && (
        <div className="mt-1 pt-1 border-t border-gray-600 text-gray-300">
          Net:{' '}
          {diffs.map((d, i) => {
            const pct = Math.round(d.diff * 100);
            const color = d.diff > 0 ? 'text-green-400' : 'text-red-400';
            return (
              <span key={d.label}>
                {i > 0 && ', '}
                <span className={color}>
                  {d.diff > 0 ? '+' : ''}{pct}% {d.label.toLowerCase()}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
